import * as React from "react";
import { useMinimumWageQuery } from "../useMinimumWageQuery";
import { MinimalWagePageProps } from "../types";
import { useMinimumWageMutation } from "../useMinimumWageMutation";
import { useLocalize } from "@/modules/shared/context/localize/LocalizeContext"
import { MinimumWageCountryLayout } from "../components/MinimumWageCountryLayout";
import { FieldName, MinimumWageOptionsSection } from "../containers/MinimumWageOptionsSection";
import { MinimumWageAllowanceExpandableItemSection } from "../containers/MinimumWageAllowanceExpandableItemSection";
import { CalculationStartDateSection } from "../containers/CalculationStartDateSection";
import { DanemarkMinimumWageConfigurationModel } from "@/app/models/configuration/minimumWage/danemarkMinimumWageConfigurationModel";
import { MinimumWagePolishOptionsSection } from "../containers/MinimumWagePolishOptionsSection";
import { GenericMinimumWageExpandableItemSection } from "../containers/GenericMinimumWageExpandableItemSection";
import { DriverQualificationsType } from "@/app/constants/driverQualificationsType";
import { PreLoader } from "../components/PreLoader";
import { getDanemarkDriverQualificationType } from "@/app/services/contract/driverQualificationTypeResolver";
import { buildMinimumWageDiffRenderers } from '../minimumWageDiffUtils'

const qualificationTypes = [
   DriverQualificationsType.NotSet, 
   DriverQualificationsType.CarriageOfGoods, 
   DriverQualificationsType.CabotageInPassengerTransport,
   DriverQualificationsType.TouristTransport,
]

const defaultFields: FieldName[] = [
    "printCalculatedHours",
    "calculateCabotageAndCrossTrade",
]

const allowedCurrenciesInner = [
    'DKK',
]

const allowedCurrencies = [
    'DKK',
    'PLN',
]

const country = 'danemark'

export const DenmarkMinimumWageSettingsPage = ({ match }: MinimalWagePageProps) => {
    const { companyId } = match.params
    const q = useMinimumWageQuery(companyId)
    const up = useMinimumWageMutation(companyId)
    const { t } = useLocalize()

    const [state, setState] = React.useState<DanemarkMinimumWageConfigurationModel | null>(null)

    const diffRenderers = React.useMemo(() => buildMinimumWageDiffRenderers('danemarkConfiguration', t), [t])
    const diffLabels = React.useMemo<Record<string, string>>(() => ({
        configuration: t('configuration.minimumWages.options'),
        expandableItems: t('configuration.minimumWages.minimalHourRateTitle'),
        allowanceExpandableItems: t('configuration.minimumWages.minimalAllowanceTitle'),
    }), [t])

    React.useEffect(() => {
        setState(null)
    }, [companyId])

    const normalizedQueryData = React.useMemo(() => {
        if (!q.data) return null
        const normalized = structuredClone(q.data.danemarkConfiguration)
        normalized.expandableItems = normalized.expandableItems.map((ei) => ({
            ...ei,
            driverQualifications: getDanemarkDriverQualificationType(ei.driverQualifications),
        }))
        return normalized
    }, [q.data])

    React.useEffect(() => {
        if (normalizedQueryData) {
            setState(normalizedQueryData)
        }
    }, [normalizedQueryData])

    if (!state || !q.data || !normalizedQueryData) {
        return <PreLoader />
    }

    const isDirty = JSON.stringify(state) !== JSON.stringify(normalizedQueryData)

    return (
        <MinimumWageCountryLayout
            selectedCountryValue={country}
            isDirty={isDirty}
            handleSave={() => up.mutateAsync({ ...q.data, danemarkConfiguration: state })}
            handleCancel={() => setState(normalizedQueryData)}
            dataTestId="denmark-minimum-wage-settings-page"
            isLoading={(q.isFetching || up.isPending)}
            diffBefore={q.data.danemarkConfiguration as Record<string, unknown>}
            diffAfter={state as Record<string, unknown>}
            diffLabels={diffLabels}
            diffRenderers={diffRenderers}
        >
            <CalculationStartDateSection
                state={state}
                setState={setState}
            />

            <div className={!state.configuration.calculateAmountsForCountry ? 'tw-opacity-50 tw-pointer-events-none' : ''}>
                    <GenericMinimumWageExpandableItemSection
                        allowedCurrencies={allowedCurrenciesInner}
                        qualificationTypes={qualificationTypes} 
                        state={state}
                        setState={setState}
                    />

                    <MinimumWageAllowanceExpandableItemSection
                        allowedCurrencies={allowedCurrencies}
                        state={state}
                        setState={setState}
                    />

                    <MinimumWageOptionsSection
                        className='tw-my-5'
                        loading={false}
                        fields={defaultFields}
                        translateKey='danemarkConfiguration'
                        state={state}
                        accommodationAndAllowanceMaxDate={q.data.generalConfiguration.accommodationAndAllowanceMaxDate}
                        setState={setState}
                    />

                    <MinimumWagePolishOptionsSection 
                        className='tw-my-5'
                        loading={false}
                        state={state}
                        setState={setState}
                    />
            </div>

            <div style={{ minHeight: '90px' }} />
        </MinimumWageCountryLayout>
    )
}
